import * as React from 'react';
import { FormControl, FormLabel, Box, IconButton, FormHelperText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import InsertScheduleIcon from '@mui/icons-material/Schedule';
import moment from 'moment';
import { useMemo, memo } from 'react';

interface TimeProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (time: moment.Moment | null) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: moment.Moment | null;
    error?: string;
    format?: string;
    ampm?: boolean;
    minutesStep?: number;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Time = memo((props: TimeProps) => {
    const {
        variant = 'outlined',
        onChange,
        onFocus,
        onKeyUp,
        label,
        description,
        required = false,
        value,
        error = '',
        ampm = false,
        minutesStep = 1,
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

// Update the validation logic in your useMemo
    const { processedError, showError } = useMemo(() => {
        let processedError = error;

        // Only show errors after submission
        if (!appValidated) {
            return { processedError: '', showError: false };
        }

        if (error?.includes('must be string')) {
            processedError = '';
        } else if (error?.includes('should match format "time"') || error?.includes('should match pattern')) {
            processedError = ampm
                ? 'Time should match 12 hour format E.G 02:10 PM'
                : 'Time should match 24 hour format E.G 14:10';
        }

        const hasValidValue = value && value.isValid();
        if (required && !hasValidValue) {
            processedError = 'This field is required';
        }

        const showError = processedError !== '';
        return { processedError, showError };
    }, [error, ampm, required, value, appValidated]);

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Memoize the openPickerButton component
    const OpenPickerButton = useMemo(() =>
            (buttonProps: any) => (
                <IconButton {...buttonProps}>
                    <InsertScheduleIcon color="disabled" />
                </IconButton>
            ),
        []
    );

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <TimePicker
                        value={value}
                        onChange={onChange}
                        ampm={ampm}
                        minutesStep={minutesStep}
                        disabled={!enabled}
                        slots={{
                            openPickerButton: OpenPickerButton
                        }}
                        slotProps={{
                            openPickerButton: {
                                style: { marginRight: 0 },
                            },
                            textField: {
                                required,
                                onFocus,
                                onKeyUp,
                                error: showError,
                                variant,
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizationProvider>
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {showError && (
                    <FormHelperText error={true}>
                        {processedError}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Time.displayName = 'Time';

export default Time;
